import { useRecordContext, useTranslate } from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { ShowButton } from "@/components/admin/show-button";

import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";
import { TagsListEdit } from "./TagsListEdit";
import { ContactStatusSelector } from "./ContactInputs";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { AsideSection } from "../misc/AsideSection";
import type { Contact } from "../types";
import { ContactMergeButton } from "./ContactMergeButton";
import { ExportVCardButton } from "./ExportVCardButton";
import { StatusBadge, TestBadge } from "../faromarket-shared/StatusBadge";

const FaroMarketContactInfo = ({ record }: { record: Contact }) => {
  if (
    !record.whatsapp &&
    !record.instagram_url &&
    !record.preferred_channel &&
    !record.acquisition_source &&
    !record.source &&
    !record.environment &&
    !record.is_test
  ) {
    return null;
  }
  return (
    <AsideSection title="FaroMarket">
      {record.whatsapp && (
        <span>
          <span className="text-muted-foreground">WhatsApp:</span>{" "}
          {record.whatsapp}
        </span>
      )}
      {record.instagram_url && (
        <a
          href={record.instagram_url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:no-underline"
        >
          Instagram
        </a>
      )}
      {record.preferred_channel && (
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Canal preferido:</span>
          <StatusBadge
            value={record.preferred_channel}
            kind="preferred_channel"
          />
        </div>
      )}
      {record.acquisition_source && (
        <span>
          <span className="text-muted-foreground">Origem:</span>{" "}
          {record.acquisition_source}
        </span>
      )}
      {record.source && record.source !== record.acquisition_source && (
        <span>
          <span className="text-muted-foreground">Source:</span> {record.source}
        </span>
      )}
      {record.environment && (
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Ambiente:</span>
          <StatusBadge value={record.environment} kind="environment" />
        </div>
      )}
      {record.is_test ? <TestBadge value={record.is_test} /> : null}
    </AsideSection>
  );
};

export const ContactAside = ({ link = "edit" }: { link?: "edit" | "show" }) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <div className="hidden sm:block w-92 min-w-92 text-sm">
      <div className="mb-4 -ml-1">
        {link === "edit" ? (
          <EditButton label="resources.contacts.action.edit" />
        ) : (
          <ShowButton label="resources.contacts.action.show" />
        )}
      </div>

      <AsideSection title={translate("resources.notes.fields.status")}>
        <ContactStatusSelector />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.personal_info")}
      >
        <ContactPersonalInfo />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.background_info")}
      >
        <ContactBackgroundInfo />
      </AsideSection>

      <FaroMarketContactInfo record={record} />

      <AsideSection
        title={translate("resources.tags.name", { smart_count: 2 })}
      >
        <TagsListEdit />
      </AsideSection>

      <AsideSection
        title={translate("resources.tasks.name", { smart_count: 2 })}
      >
        <ReferenceManyField
          target="contact_id"
          reference="tasks"
          sort={{ field: "due_date", order: "ASC" }}
          perPage={1000}
        >
          <TasksIterator />
        </ReferenceManyField>
        <AddTask />
      </AsideSection>

      {link !== "edit" && (
        <>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <ExportVCardButton />
            <ContactMergeButton />
          </div>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <DeleteButton
              className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
              size="sm"
            />
          </div>
        </>
      )}
    </div>
  );
};
